import React, { useState, useEffect } from 'react';
import { Image, Search, RefreshCw, Trash2, ArrowLeft, Image as ImageIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ImageRecycleBinPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchDeletedImages();
  }, []);

  const fetchDeletedImages = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/images?deleted=true&view_scope=all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
      }
    } catch (err) {
      console.error('Failed to fetch deleted images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      const res = await fetch(`/api/images/${id}/restore`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        fetchDeletedImages();
      }
    } catch (err) {
      console.error('Failed to restore image:', err);
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('确定要彻底删除这张图片吗？（操作不可恢复）')) return;
    try {
      const res = await fetch(`/api/images/${id}/hard`, { 
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        fetchDeletedImages();
      }
    } catch (err) {
      console.error('Failed to permanently delete image:', err);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/content/images" className="p-2 text-gray-500 hover:text-gray-900 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-800">
          <Trash2 className="w-6 h-6 text-red-500" />
          图片回收站
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">加载中...</div>
        ) : images.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 font-medium">预览图</th>
                  <th className="px-6 py-3 font-medium">提示词(Prompt)</th>
                  <th className="px-6 py-3 font-medium">删除时间</th>
                  <th className="px-6 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {images.map(image => (
                  <tr key={image.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      {image.imageUrl ? (
                        <div 
                          className="w-16 h-16 rounded object-cover overflow-hidden bg-gray-100 border border-gray-200 opacity-80 cursor-pointer hover:opacity-100 transition-opacity ring-2 ring-transparent hover:ring-blue-400"
                          onClick={() => setSelectedImage(image.imageUrl)}
                          title="点击查看大图"
                        >
                          <img src={image.imageUrl.startsWith('http') ? image.imageUrl : `${image.imageUrl}`} alt="预览" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded bg-gray-100 border border-gray-200 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-500 line-through max-w-xs truncate">
                      {image.prompt || '无提示词'}
                    </td>
                    <td className="px-6 py-4 text-gray-500">{new Date(image.updatedAt).toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleRestore(image.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          恢复
                        </button>
                        <button 
                          onClick={() => handlePermanentDelete(image.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          彻底删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 mb-4">
              <Trash2 className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-lg font-medium text-gray-900 mb-1">回收站是空的</p>
            <p className="text-sm text-gray-400">没有被删除的图片</p>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] flex flex-col items-center">
            <button 
              className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full hover:bg-black/80 transition-colors"
              onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
              title="关闭"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={selectedImage.startsWith('http') ? selectedImage : `${selectedImage}`} 
              alt="大图预览" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
